import React from 'react'
import { useList } from '@refinedev/core'
import { Collapse, Typography } from 'antd'
import { Link } from 'react-router'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, Pill, EmptyHint, type Stat } from '../../theme/listkit'
import { ShieldAlert, ShieldCheck, ScanLine } from '../../components/icons'
import { describeAction } from './describeAction'

interface ModerationAction {
  id: string
  actor_user_id: string | null
  target_user_id: string | null
  action: string
  reason: string | null
  created_at: string
  /** Feed actions target an ACTIVITY or a RANGE, so everything identifying the
   *  target — including feed-purge's `deleted_ids` — lives here, not in
   *  target_user_id (which is deliberately null for them). */
  metadata: Record<string, unknown> | null
}

const fmtWhen = (v: string | null) =>
  v
    ? new Date(v).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—'

const mono: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 600,
  fontSize: 12.5,
  color: brand.ink,
}

/**
 * Moderation Log — read-only audit trail of admin integrity actions written by
 * the admin-action Edge Function (member suspensions, Stream bans) AND by
 * feed-moderate / feed-purge (single and bulk feed removals),
 * rendered as a ceremonial audit timeline. This single useList is the only data
 * source, so liveMode 'off' is REQUIRED to avoid a double subscription.
 */
export function ModerationActionList() {
  // Audit trail: BOUNDED pageSize (the log is small and read-only; 200 >> the
  // realistic action count) so it never issues an unbounded fetch.
  const { result } = useList<ModerationAction>({
    resource: 'moderation_actions',
    pagination: { pageSize: 200 },
    liveMode: 'off',
    sorters: [{ field: 'created_at', order: 'desc' }],
  })
  const rows = result?.data ?? []

  // Deck counts come from BOUNDED pageSize:1 queries (PostgREST returns the
  // exact `total` per filter without shipping rows) rather than filtering the
  // whole table on the main thread. No admin_moderation_actions_summary view
  // exists and these are plain COUNT(filter) numbers, so count queries fit.
  const { result: totalRes } = useList<ModerationAction>({
    resource: 'moderation_actions',
    pagination: { pageSize: 1 },
    liveMode: 'off',
  })
  const { result: suspendRes } = useList<ModerationAction>({
    resource: 'moderation_actions',
    pagination: { pageSize: 1 },
    liveMode: 'off',
    filters: [{ field: 'action', operator: 'eq', value: 'suspend' }],
  })
  const { result: streamRes } = useList<ModerationAction>({
    resource: 'moderation_actions',
    pagination: { pageSize: 1 },
    liveMode: 'off',
    filters: [{ field: 'action', operator: 'startswith', value: 'stream' }],
  })
  const totalActions = totalRes?.total ?? 0
  const suspensions = suspendRes?.total ?? 0
  const streamBans = streamRes?.total ?? 0

  const stats: Stat[] = [
    { label: 'Total Actions', value: totalActions.toLocaleString(), icon: ShieldCheck, tone: 'jaguar' },
    { label: 'Suspensions', value: suspensions.toLocaleString(), icon: ShieldAlert, tone: suspensions > 0 ? 'red' : 'neutral' },
    { label: 'Stream Bans', value: streamBans.toLocaleString(), icon: ScanLine, tone: 'jaguar' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Trust & Safety · Feed Moderation',
        description:
          'A tamper-evident record of every integrity action taken — suspensions and Stream bans against members, and removals from the community feed — with the admin, what was affected, and the reason behind each call. Feed removals list the activity ids they touched; for a reversible removal those ids are what you restore from.',
      }}
      stats={stats}
      statColumns={3}
    >
      <SectionCard title="Moderation Audit Trail" sub="Every action · newest first">
        {rows.length === 0 ? (
          <EmptyHint>No moderation actions recorded yet.</EmptyHint>
        ) : (
          <div>
            {rows.map((m, i) => {
              const d = describeAction(m.action, m.metadata, m.target_user_id)
              return (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  padding: '14px 0',
                  borderBottom: i < rows.length - 1 ? '1px solid ' + brand.cardLine : 'none',
                }}
              >
                <div style={{ flexShrink: 0, paddingTop: 1 }}>
                  <Pill tone={d.tone}>{m.action}</Pill>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                    <span style={d.isFeedAction ? { ...mono, fontWeight: 500 } : mono}>{d.summary}</span>
                    <span style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12, color: brand.sub }}>
                      by
                    </span>
                    <span style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12.5, color: brand.jaguar }}>
                      {m.actor_user_id ?? '—'}
                    </span>
                  </div>

                  {d.detail && (
                    <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12.5, color: brand.sub, marginTop: 3 }}>
                      {d.detail}
                    </div>
                  )}

                  {d.ids.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <Collapse
                        ghost
                        size="small"
                        items={[
                          {
                            key: 'ids',
                            label: (
                              <span style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.jaguar }}>
                                {d.ids.length === 1 ? '1 activity id' : `${d.ids.length} activity ids`}
                                {d.recoverable ? ' — the key to restoring these' : ''}
                              </span>
                            ),
                            children: (
                              <>
                                <Typography.Paragraph
                                  copyable={{ text: d.ids.join('\n') }}
                                  style={{
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    fontSize: 11.5,
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                    background: brand.paper,
                                    border: '1px solid ' + brand.cardLine,
                                    borderRadius: 8,
                                    padding: '8px 10px',
                                    marginBottom: 6,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {d.ids.join('\n')}
                                </Typography.Paragraph>
                                <div style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub }}>
                                  {d.recoverable ? (
                                    <>
                                      Still restorable. Open{' '}
                                      <Link to="/feed-moderation">Feed Moderation</Link>, turn on
                                      “Show removed items”, and set the date range to the window
                                      above — the removed posts appear there and can be restored one
                                      at a time.
                                    </>
                                  ) : (
                                    'Permanently deleted from GetStream. These ids are a record of what was removed, not a way to bring it back.'
                                  )}
                                </div>
                              </>
                            ),
                          },
                        ]}
                      />
                    </div>
                  )}

                  <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.sub, marginTop: 4 }}>
                    {m.reason ?? <span style={{ color: brand.sub }}>No reason given</span>}
                  </div>
                </div>
                <span
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 500,
                    fontSize: 11.5,
                    color: brand.sub,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmtWhen(m.created_at)}
                </span>
              </div>
              )
            })}
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}
